import { useState } from 'react';
import _ from 'lodash';
import i18next from 'i18next';
import { ReportWLOfferListViewFragment } from '__generated__/graphql';
import { tableData, tableHeaders } from './useReportData';
import { formatNumber } from 'semshared/utils/format';
import { NO_PRICE } from 'semshared/pricelist/quickprice';

type UseReportExportArgs = {
  reportResults: ReportWLOfferListViewFragment[];
  fetchAllReportResults?: () => Promise<ReportWLOfferListViewFragment[]>;
};

/**
 * Hook for exporting report data to CSV format.
 * Supports fetching all results via callback for complete dataset export.
 */
export function useReportExport({ reportResults, fetchAllReportResults }: UseReportExportArgs) {
  const [exportLoading, setExportLoading] = useState(false);

  const handleExportExcel = async () => {
    if (exportLoading) {
      return;
    }

    setExportLoading(true);

    try {
      console.log('handleExportExcel: Starting export, fetchAllReportResults:', !!fetchAllReportResults);

      const dataToExport = fetchAllReportResults ? await fetchAllReportResults() : reportResults;

      console.log('handleExportExcel: Data received:', dataToExport?.length || 0, 'results');

      if (!dataToExport || !dataToExport.length) {
        alert(String(i18next.t('report-no-export-data')));
        return;
      }

      const csvContent = generateCSV(dataToExport);
      downloadCSV(csvContent, `booking-report-${new Date().toISOString().split('T')[0]}.csv`);
    } catch (error) {
      console.error('Report export error:', error);
      const errorMessage = error instanceof Error ? error.message : String(i18next.t('generic-error-unknown'));
      alert(String(i18next.t('report-export-failed', { message: errorMessage })));
    } finally {
      setExportLoading(false);
    }
  };

  const generateCSV = (data: ReportWLOfferListViewFragment[]) => {
    const headers = tableHeaders.filter(header => !header.skipCsv);
    const rawData = tableData(data);

    // Fields that should be formatted as numbers
    const numberFields = [
      '_singleRooms',
      '_doubleRooms',
      '_guests',
      'latestVersion.totalPriceNet',
      'latestVersion.totalPriceGross',
      'commissionAmountNet',
    ];
    // Fields that should be formatted as dates (already formatted in tableData)
    const dateFields = ['_createdAt', '_seminarStartDate', '_seminarEndDate'];

    const csvData = rawData.map(result => {
      return headers.map(header => {
        const value = _.get(result, header.field);

        if (numberFields.includes(header.field)) {
          if (typeof value !== 'number' || !Number.isFinite(value) || value === NO_PRICE) {
            return 'N/A';
          }
          return formatNumber(value);
        }

        if (value === null || value === undefined || value === '') {
          return 'N/A';
        }
        // Dates are already formatted in tableData
        if (dateFields.includes(header.field)) {
          return value;
        }

        return String(value);
      });
    });

    return [headers.map(header => header.label), ...csvData].map(row => row.map(cell => `"${String(cell)}"`).join(',')).join('\n');
  };

  const downloadCSV = (csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return { handleExportExcel, exportLoading };
}
